import { Economy, LevelCatalog } from "@highmarch/app";
import {
  BalanceData, bossFirstClears, maxBuilders, resourcePackContents, secondsToNextDay,
} from "@highmarch/domain";

/**
 * The Chests & Shop page as data (FINISH_PLAN WP5; ADR 0013; D6, D9): what each card shows and whether it can be
 * bought now, read from the economy (@highmarch/app Economy) with no DOM, so the rules the page shows are testable
 * (shopModel.test.ts): the crate a card previews is the crate a purchase grants, affordability, the daily caps, and
 * the post-loss quiet (`nudges`: after a loss the page is still there, but it pushes nothing — no highlight, no "get
 * gems" jump; ENGAGEMENT C).
 */

/** A price in gems and whether the wallet covers it. */
export interface GemPrice { readonly gems: number; readonly affordable: boolean; readonly short: number }

export interface BuilderCard {
  /** The builder the purchase would add (2–4). */
  readonly next: number;
  readonly owned: number;
  readonly max: number;
  readonly price: GemPrice;
}

export interface PackCard {
  readonly id: string;
  readonly kind: "speedUp" | "resources";
  /** Minutes of speed-up (a speed-up pack), or 0. */
  readonly minutes: number;
  /** Resources it adds now, at the player's level (a resource pack), or null. */
  /** Gold (a resource pack); null for a speed-up pack. */
  readonly contents: number | null;
  readonly price: GemPrice;
  /** Buys left today (resource packs are capped a day), or null for no cap. */
  readonly left: number | null;
  readonly perDay: number | null;
  /** The pack's own title (MP-07 War Coffers), or null. */
  readonly name: string | null;
  /** Its painted icon id (`res_<icon>`): "gold", or War Coffers' "gold_coffers"; "speedup" for a speed-up pack. */
  readonly icon: string;
  /** The level it opens at (the lock chip), or null when it is open. */
  readonly opensAt: number | null;
  /** Today's free claim of this pack (owner 2026-10-21): a separate FREE tile ahead of the row; null when it has none. */
  readonly free?: { readonly open: boolean } | null;
}

export interface GemPackCard { readonly id: string; readonly name: string; readonly gems: number; readonly referencePrice: string; readonly tier: number }

/** A line of the "Where gems come from" panel (M23): the free sources, from balance. */
export interface GemSourceLine { readonly key: string; readonly title: string; readonly detail: string }

export interface ShopModel {
  readonly gems: number;
  /** null once every builder is owned. */
  readonly builder: BuilderCard | null;
  readonly speedUps: readonly PackCard[];
  readonly resourcePacks: readonly PackCard[];
  readonly gemPacks: readonly GemPackCard[];
  readonly gemSources: readonly GemSourceLine[];
  /** Seconds until the daily caps and the free gold pack reset (UTC midnight). */
  readonly resetIn: number;
  /** Whether the page may push anything (a highlight, a jump to gem packs): false in the quiet after a loss. */
  readonly nudges: boolean;
}

export function gemPrice(gems: number, have: number): GemPrice {
  return { gems, affordable: have >= gems, short: Math.max(0, gems - have) };
}

export function shopModel(catalog: LevelCatalog, now: number): ShopModel {
  const B = catalog.balance, eco = new Economy(catalog), wallet = eco.wallet, have = wallet.gems;
  const shop = B.commerce.shop, state = catalog.economy.shop;
  const nextPrice = eco.nextBuilderPrice();
  const builder: BuilderCard | null = nextPrice === null ? null
    : { next: catalog.profile.buildersOwned + 1, owned: catalog.profile.buildersOwned, max: maxBuilders(B), price: gemPrice(nextPrice, have) };
  const speedUps: PackCard[] = shop.speedUpPacks.map((p) => ({ id: p.id, kind: "speedUp", minutes: p.minutes, contents: null, price: gemPrice(p.gems, have), left: null, perDay: null, name: null, icon: "speedup", opensAt: null }));
  const freeGold = eco.freePack(now);
  const freeTile: PackCard[] = freeGold ? [{ id: freeGold.id, kind: "resources", minutes: 0, contents: freeGold.gold, price: gemPrice(0, have), left: null, perDay: null, name: null, icon: "gold", opensAt: null, free: { open: freeGold.open } }] : [];
  const resourcePacks: PackCard[] = [...freeTile, ...shop.resourcePacks.map((p): PackCard => ({
    id: p.id, kind: "resources", minutes: 0, contents: resourcePackContents(B, p.id, eco.level), price: gemPrice(p.gems, have),
    left: Math.max(0, state.packCap(p.perDay) - state.packsBought(now, p.id)), perDay: state.packCap(p.perDay), name: p.name, icon: p.icon,
    opensAt: catalog.highestLevelCleared < p.unlockAfterLevel ? p.unlockAfterLevel + 1 : null,
  }))];
  return {
    gems: have, builder, speedUps, resourcePacks,
    gemPacks: shop.gemPacks.map((p, i) => ({ id: p.id, name: p.name, gems: p.gems, referencePrice: p.referencePrice, tier: i })),
    gemSources: gemSources(B), resetIn: secondsToNextDay(now), nudges: eco.offersAllowed("hub", now),
  };
}

/** The free gem sources (D20) with their sizes from balance: what a player can earn without paying. */
export function gemSources(B: BalanceData): GemSourceLine[] {
  const out: GemSourceLine[] = [];
  const stars = B.commerce.gems.starChests.filter((g) => g > 0);
  if (stars.length > 0) out.push({ key: "stars", title: "Star chests", detail: `${stars.join(" · ")} gems per chapter` });
  const bosses = bossFirstClears(B).filter((b) => b.gems > 0);
  if (bosses.length > 0) {
    const each = [...new Set(bosses.map((b) => b.gems))];
    out.push({ key: "bosses", title: "Boss first wins", detail: each.length === 1 ? `${each[0]} gems for each of ${bosses.length} bosses` : `${bosses.reduce((n, b) => n + b.gems, 0)} gems over ${bosses.length} bosses` });
  }
  if (B.orders.weeklyGems > 0) out.push({ key: "weekly", title: "Weekly Orders chest", detail: `${B.orders.weeklyGems} gems for ${B.orders.weeklyGoal} orders in a week` });
  const checkin = B.orders.checkinSlots.map((s, i) => ({ day: i + 1, gems: s.gems })).filter((s) => s.gems > 0);
  if (checkin.length > 0) out.push({ key: "checkin", title: "Daily check-in", detail: checkin.map((c) => `Day ${c.day}: ${c.gems} gems`).join(" · ") });
  out.push({ key: "more", title: "Events and achievements", detail: "Gems on many a reward" });
  return out;
}

/** "5h", "42m": how long until the daily caps reset (no seconds: this is not a countdown to push anyone). */
export function fmtReset(seconds: number): string {
  const SECONDS_PER_HOUR = 3600, SECONDS_PER_MINUTE = 60;
  const h = Math.floor(seconds / SECONDS_PER_HOUR);
  return h >= 1 ? `${h}h` : `${Math.max(1, Math.ceil(seconds / SECONDS_PER_MINUTE))}m`;
}

/** A gold pack with its own name (MP-07's War Coffers) as the Gold page's "More gold" row shows it. */
export interface NamedPackLine { readonly id: string; readonly name: string; readonly icon: string; readonly gold: number; readonly gems: number; readonly perDay: number; readonly opensAt: number | null }

export function namedGoldPacks(catalog: LevelCatalog): NamedPackLine[] {
  const B = catalog.balance, level = Math.max(1, catalog.highestLevelCleared);
  return B.commerce.shop.resourcePacks.filter((p) => p.name !== null).map((p) => ({
    id: p.id, name: p.name!, icon: p.icon, gold: resourcePackContents(B, p.id, level) ?? 0, gems: p.gems, perDay: p.perDay,
    opensAt: catalog.highestLevelCleared < p.unlockAfterLevel ? p.unlockAfterLevel + 1 : null,
  }));
}
